/**
 * Valma (refonte 2026) : comportements des blocs sur mesure.
 * - Rafraîchit les éléments [data-valma-refresh] quand la variante change (événement Shopify « product:select »).
 * - <valma-combo> : onglets, sélection des bracelets, total, ajout de tout le combo au panier.
 */
(() => {
  const formatMoney = (cents, format) => {
    const value = (cents / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const withComma = `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${dec}`;
    // Format français Valma : « 89 € », « 201,45 € » (indépendant du format de la boutique).
    return `${dec === '00' ? int.replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f') : withComma} €`;
  };

  // Ajout au panier Valma (ajout rapide, combos) : comme le formulaire produit Horizon, on demande à Shopify le HTML
  // à jour des sections panier puis on émet l'événement panier standard → le tiroir et la pastille se mettent à jour
  // et le tiroir s'ouvre. Sans ça, le tiroir garde le contenu du chargement de la page.
  const valmaAddToCart = async (items, source) => {
    const root = window.Shopify?.routes?.root || '/';
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId);
    let events = null;
    try { events = await import('@shopify/events'); } catch (error) { console.warn('[valma] événements panier indisponibles', error); }
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
    promise.catch(() => {});
    if (events?.CartLinesUpdateEvent) {
      source.dispatchEvent(new events.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise,
      }));
    }
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Ajout impossible');
      const cart = await (await fetch(`${root}cart.js`)).json();
      resolve({
        cart: events?.CartLinesUpdateEvent?.createCartFromAjaxResponse ? events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : cart,
        detail: { items: cart.items, source: 'valma', itemCount: items.reduce((n, i) => n + i.quantity, 0), sections: data.sections, didError: false },
      });
      if (!events) document.querySelectorAll('cart-icon').forEach((icon) => icon.renderCartBubble?.(cart.item_count));
      return cart;
    } catch (error) {
      reject(error);
      throw error;
    }
  };
  window.valmaAddToCart = valmaAddToCart;

  class ValmaCombo extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick = (event) => {
      const tab = event.target.closest('[data-tab]');
      if (tab) {
        this.querySelectorAll('[data-tab]').forEach((t) => t.classList.toggle('is-active', t === tab));
        this.querySelectorAll('[data-panel]').forEach((p) => p.classList.toggle('is-active', p.dataset.panel === tab.dataset.tab));
        return;
      }
      const toggle = event.target.closest('.valma-combo__toggle');
      if (toggle) {
        const pressed = toggle.getAttribute('aria-pressed') !== 'true';
        this.querySelectorAll(`.valma-combo__toggle[data-variant-id="${toggle.dataset.variantId}"]`).forEach((t) => {
          t.setAttribute('aria-pressed', String(pressed));
          t.textContent = pressed ? '✓ Ajouté' : '+ Ajouter';
        });
        this.update();
        return;
      }
      if (event.target.closest('[data-add]')) this.addToCart(event.target.closest('[data-add]'));
    };

    selected() {
      const seen = new Map();
      this.querySelectorAll('.valma-combo__toggle[aria-pressed="true"]').forEach((t) => seen.set(t.dataset.variantId, t));
      return [...seen.values()];
    }

    update() {
      const items = this.selected();
      const count = items.length + 1;
      const total = Number(this.dataset.currentPrice) + items.reduce((sum, t) => sum + Number(t.dataset.price), 0);
      const discount = count >= Number(this.dataset.threshold) ? Number(this.dataset.discount) : 0;
      const final = Math.round(total * (1 - discount / 100));
      const fmt = this.dataset.moneyFormat;
      const countEl = this.querySelector('[data-count]');
      if (countEl) countEl.textContent = count;
      const pluralEl = this.querySelector('[data-plural]');
      if (pluralEl) pluralEl.textContent = count > 1 ? 's' : '';
      this.querySelector('[data-total]').textContent = formatMoney(final, fmt);
      const before = this.querySelector('[data-before]');
      before.hidden = !discount;
      before.textContent = discount ? formatMoney(total, fmt) : '';
      this.querySelector('[data-thumbs]').innerHTML = items
        .map((t) => `<img src="${t.dataset.image}" alt="" width="34" height="34">`)
        .join('');
    }

    async addToCart(button) {
      const items = [{ id: Number(this.dataset.currentVariant), quantity: 1 }].concat(
        this.selected().map((t) => ({ id: Number(t.dataset.variantId), quantity: 1 }))
      );
      const label = button.textContent;
      button.disabled = true;
      button.textContent = 'Ajout…';
      try {
        await valmaAddToCart(items, this);
        button.textContent = '✓ Ajouté';
      } catch (error) {
        console.error(error);
        button.textContent = 'Réessayer';
      }
      setTimeout(() => { button.disabled = false; button.textContent = label; }, 2000);
    }
  }

  if (!customElements.get('valma-combo')) customElements.define('valma-combo', ValmaCombo);

  // Combo de la home (section valma-split) : ajoute les 3 bracelets affichés
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-valma-add-combo]');
    if (!button) return;
    const scope = button.closest('.valma-split__txt');
    const items = [...scope.querySelectorAll('[data-variant]')].map((a) => ({ id: Number(a.dataset.variant), quantity: 1 }));
    const label = button.textContent;
    button.disabled = true;
    try {
      await valmaAddToCart(items, button);
      button.textContent = '✓ Combo ajouté';
    } catch (error) {
      console.error(error);
      button.textContent = 'Réessayer';
    }
    setTimeout(() => { button.disabled = false; button.textContent = label; }, 2000);
  });

  // Ajout rapide depuis les cartes produit (pages catégorie) : choix de la taille puis ajout au panier.
  document.addEventListener('click', async (event) => {
    const size = event.target.closest('.valma-rcard__sizes button, .valma-gcard__sizes button');
    if (size) {
      const quick = size.closest('.valma-rcard__quick, .valma-gcard__quick');
      quick.querySelectorAll('.valma-rcard__sizes button, .valma-gcard__sizes button').forEach((b) => b.setAttribute('aria-pressed', String(b === size)));
      const add = quick.querySelector('[data-valma-quick-add]');
      add.dataset.variantId = size.dataset.variantId;
      add.querySelector('span').textContent = size.dataset.price;
      return;
    }
    const add = event.target.closest('[data-valma-quick-add]');
    if (!add) return;
    const label = add.innerHTML;
    add.disabled = true;
    try {
      await valmaAddToCart([{ id: Number(add.dataset.variantId), quantity: 1 }], add);
      add.textContent = '✓ Ajouté';
    } catch (error) {
      console.error(error);
      add.textContent = 'Réessayer';
    }
    setTimeout(() => { add.innerHTML = label; add.disabled = false; }, 1800);
  });

  // Galerie fiche produit : la photo glisse au survol d'une vignette (desktop) et au toucher (mobile :
  // vignettes, points, ou la photo elle-même pour passer à la suivante). Pastille « Voir porté ».
  const showSlide = (gallery, index) => {
    const slides = gallery.querySelectorAll('.valma-gal__slide');
    if (!slides.length) return;
    const i = (index + slides.length) % slides.length;
    gallery.querySelector('.valma-gal__track').style.transform = `translateX(-${i * 100}%)`;
    gallery.dataset.index = String(i);
    gallery.querySelectorAll('.valma-gal__thumbs button, .valma-gal__dots button').forEach((b) => {
      b.classList.toggle('is-on', Number(b.dataset.index) === i);
    });
  };
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  document.addEventListener('mouseover', (event) => {
    if (!canHover.matches) return;
    const thumb = event.target.closest?.('.valma-gal__thumbs button');
    if (thumb) showSlide(thumb.closest('.valma-gal'), Number(thumb.dataset.index));
  });
  document.addEventListener('click', (event) => {
    const gallery = event.target.closest('.valma-gal');
    if (!gallery) return;
    const control = event.target.closest('.valma-gal__thumbs button, .valma-gal__dots button, [data-valma-worn]');
    if (control) return showSlide(gallery, Number(control.dataset.index));
    if (!canHover.matches && event.target.closest('.valma-gal__track')) {
      if (gallery.dataset.swiped) { delete gallery.dataset.swiped; return; }
      showSlide(gallery, Number(gallery.dataset.index || 0) + 1);
    }
  });

  // Rails horizontaux [data-valma-drag] : glisser à la souris (le toucher reste natif), aimantation sur la carte
  // la plus proche au relâchement, pas de clic sur un produit après un glissé. La bulle « swipe » suit le curseur.
  const initDragRail = (rail) => {
    if (rail.dataset.dragReady) return;
    rail.dataset.dragReady = 'true';
    let down = false;
    let moved = false;
    let startX = 0;
    let startLeft = 0;
    let samples = [];
    let glide = 0;

    // Élan après relâchement : la vitesse du geste décroît progressivement (défilement libre, comme Rhode)
    const momentum = (velocity) => {
      cancelAnimationFrame(glide);
      let v = velocity;
      let last = performance.now();
      const step = (now) => {
        const dt = Math.min(32, now - last);
        last = now;
        rail.scrollLeft -= v * dt;
        v *= Math.pow(0.94, dt / 16);
        const atEdge = rail.scrollLeft <= 0 || rail.scrollLeft >= rail.scrollWidth - rail.clientWidth;
        if (Math.abs(v) > 0.02 && !atEdge) glide = requestAnimationFrame(step);
        else rail.classList.remove('is-dragging');
      };
      glide = requestAnimationFrame(step);
    };

    rail.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      cancelAnimationFrame(glide);
      down = true;
      moved = false;
      startX = event.clientX;
      startLeft = rail.scrollLeft;
      samples = [{ x: event.clientX, t: performance.now() }];
    });
    window.addEventListener('pointermove', (event) => {
      if (!down) return;
      const dx = event.clientX - startX;
      if (!moved && Math.abs(dx) > 5) {
        moved = true;
        rail.classList.add('is-dragging');
      }
      if (!moved) return;
      rail.scrollLeft = startLeft - dx;
      const now = performance.now();
      samples.push({ x: event.clientX, t: now });
      samples = samples.filter((s) => now - s.t < 100);
    });
    window.addEventListener('pointerup', () => {
      if (!down) return;
      down = false;
      if (!moved) return;
      const first = samples[0];
      const last = samples[samples.length - 1];
      const dt = last && first ? last.t - first.t : 0;
      const velocity = dt > 0 && performance.now() - last.t < 80 ? (last.x - first.x) / dt : 0;
      if (Math.abs(velocity) > 0.05) momentum(velocity);
      else rail.classList.remove('is-dragging');
    });
    rail.addEventListener('click', (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    }, true);
    rail.addEventListener('dragstart', (event) => event.preventDefault());

    const wrap = rail.parentElement;
    const bubble = wrap.querySelector('.valma-rail__swipe');
    if (bubble && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      rail.addEventListener('pointermove', (event) => {
        const box = wrap.getBoundingClientRect();
        wrap.style.setProperty('--swipe-x', `${event.clientX - box.left}px`);
        wrap.style.setProperty('--swipe-y', `${event.clientY - box.top}px`);
        wrap.classList.add('is-follow');
      });
      rail.addEventListener('pointerleave', () => { if (!down) wrap.classList.remove('is-follow'); });
    }
  };
  const initDragRails = (root = document) => root.querySelectorAll('[data-valma-drag]').forEach(initDragRail);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initDragRails());
  else initDragRails();
  document.addEventListener('shopify:section:load', (event) => initDragRails(event.target));

  // Galerie fiche produit : glisser au doigt (mobile) d'une photo à l'autre
  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'touch') return;
    const track = event.target.closest('.valma-gal__track');
    if (!track) return;
    const gallery = track.closest('.valma-gal');
    const count = gallery.querySelectorAll('.valma-gal__slide').length;
    if (count < 2) return;
    const start = { x: event.clientX, y: event.clientY };
    const index = Number(gallery.dataset.index || 0);
    const width = track.getBoundingClientRect().width;
    let dx = 0;
    let horizontal = null;
    const move = (e) => {
      dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (horizontal === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) horizontal = Math.abs(dx) > Math.abs(dy);
      if (!horizontal) return;
      track.style.transition = 'none';
      const resist = (index === 0 && dx > 0) || (index === count - 1 && dx < 0) ? 0.35 : 1;
      track.style.transform = `translateX(${-index * width + dx * resist}px)`;
    };
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      track.style.transition = '';
      if (!horizontal) return;
      gallery.dataset.swiped = '1';
      const next = Math.abs(dx) > width * 0.18 ? index + (dx < 0 ? 1 : -1) : index;
      showSlide(gallery, Math.max(0, Math.min(count - 1, next)));
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  });

  // Aperçu collier + breloque (géométrie de l'ancien configurateur : ancrage cx / yb du fermoir, breloque accrochée
  // avec un chevauchement ; hauteur = scale × largeur du collier). Se recompose dès qu'on change de pierre
  // ou de breloque, sans attendre le rechargement de la fiche.
  class ValmaCollierPreview extends HTMLElement {
    connectedCallback() {
      try { this.config = JSON.parse(this.dataset.config); } catch (e) { return; }
      this.collier = this.dataset.collier;
      this.breloque = this.dataset.breloque;
      this.onChange = (event) => {
        const input = event.target.closest?.('input[data-option-name]');
        if (!input || !input.checked) return;
        if (input.dataset.optionName === this.config.option) this.collier = input.value;
        else if (input.dataset.optionName === 'Breloque') this.breloque = input.value;
        else return;
        this.render();
      };
      document.addEventListener('change', this.onChange);
      this.render();
    }
    disconnectedCallback() { document.removeEventListener('change', this.onChange); }
    render() {
      const cfg = this.config;
      const c = cfg.colliers[this.collier] || Object.values(cfg.colliers)[0];
      const b = cfg.breloques[this.breloque] || Object.values(cfg.breloques)[0];
      if (!c || !b) return;
      const stage = this.querySelector('.valma-cp__stage');
      const collierImg = this.querySelector('.valma-cp__collier');
      const brelImg = this.querySelector('.valma-cp__breloque');
      const H = c.h * (c.hf || cfg.heightFactor || 1.3);
      stage.style.aspectRatio = `${c.w} / ${H}`;
      const brelH = cfg.scale ? cfg.scale * c.w : c.rw * c.w * cfg.ratio;
      const brelW = (b.w / b.h) * brelH;
      Object.assign(collierImg.style, { left: '0', top: '0', width: '100%', height: `${(c.h / H) * 100}%` });
      Object.assign(brelImg.style, {
        left: `${((c.cx * c.w - brelW / 2) / c.w) * 100}%`,
        top: `${((c.yb * c.h - cfg.overlap * brelH) / H) * 100}%`,
        width: `${(brelW / c.w) * 100}%`,
        height: `${(brelH / H) * 100}%`,
      });
      this.classList.remove('is-ready');
      const done = () => { if (collierImg.complete && brelImg.complete) this.classList.add('is-ready'); };
      collierImg.onload = done;
      brelImg.onload = done;
      if (collierImg.getAttribute('src') !== c.src) collierImg.src = c.src;
      if (brelImg.getAttribute('src') !== b.src) brelImg.src = b.src;
      brelImg.alt = this.breloque || '';
      done();
    }
  }
  if (!customElements.get('valma-collier-preview')) customElements.define('valma-collier-preview', ValmaCollierPreview);

  // Menu mobile : onglets Bracelets / Colliers / Breloques
  document.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-valma-mtab]');
    if (!tab) return;
    const menu = tab.closest('.valma-mmenu');
    menu.querySelectorAll('[data-valma-mtab]').forEach((t) => {
      const on = t === tab;
      t.classList.toggle('is-on', on);
      t.setAttribute('aria-selected', String(on));
    });
    menu.querySelectorAll('[data-valma-mpanel]').forEach((p) => { p.hidden = p.dataset.valmaMpanel !== tab.dataset.valmaMtab; });
  });

  // Diaporama des sections bento (photos de colliers, photos du combo) : fondu, points, flèches, défilement auto
  // (3 s, en pause au survol), glisser au doigt. Délégué : les diaporamas reconstruits (combo) fonctionnent sans réinit.
  const SLIDE_DELAY = 3000;
  const slideTimers = new WeakMap();
  const slidesOf = (root) => [...root.querySelectorAll('.valma-split__slide')];
  const showSlide2 = (root, i) => {
    const slides = slidesOf(root);
    if (!slides.length) return;
    const index = (i + slides.length) % slides.length;
    root.dataset.index = String(index);
    slides.forEach((s, n) => { s.classList.toggle('is-on', n === index); if (s.tagName === 'A') s.tabIndex = n === index ? 0 : -1; });
    root.querySelectorAll('.valma-split__dots button').forEach((d, n) => d.classList.toggle('is-on', n === index));
    root.dispatchEvent(new CustomEvent('valma:slide', { detail: { index }, bubbles: true }));
  };
  // Pas de défilement auto tant que le curseur est sur la photo, ni après que le client a navigué lui-même
  const playSlides = (root) => {
    clearInterval(slideTimers.get(root));
    if (root.dataset.manual || root.matches(':hover')) return;
    if (slidesOf(root).length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    slideTimers.set(root, setInterval(() => showSlide2(root, Number(root.dataset.index || 0) + 1), SLIDE_DELAY));
  };
  const initSlides = (root) => {
    if (root.dataset.slidesReady) return;
    root.dataset.slidesReady = '1';
    root.addEventListener('mouseenter', () => clearInterval(slideTimers.get(root)));
    root.addEventListener('mouseleave', () => playSlides(root));
    let startX = null;
    root.addEventListener('touchstart', (event) => { startX = event.touches[0].clientX; }, { passive: true });
    root.addEventListener('touchend', (event) => {
      if (startX === null) return;
      const dx = event.changedTouches[0].clientX - startX;
      startX = null;
      if (Math.abs(dx) > 40) { root.dataset.manual = '1'; clearInterval(slideTimers.get(root)); showSlide2(root, Number(root.dataset.index || 0) + (dx < 0 ? 1 : -1)); }
    });
    playSlides(root);
  };
  document.addEventListener('click', (event) => {
    const control = event.target.closest('[data-valma-slides] .valma-split__arrow, [data-valma-slides] .valma-split__dots button');
    if (!control) return;
    event.preventDefault();
    const root = control.closest('[data-valma-slides]');
    const index = Number(root.dataset.index || 0);
    root.dataset.manual = '1';
    clearInterval(slideTimers.get(root));
    showSlide2(root, control.dataset.dir ? index + Number(control.dataset.dir) : Number(control.dataset.index));
  });
  // Remplace les photos d'un diaporama (combo : photos portées de la pierre choisie)
  const setSlides = (root, photos, alt) => {
    root.innerHTML = photos.map((p, i) => `<div class="valma-split__slide${i === 0 ? ' is-on' : ''}"><img src="${p.src}"${p.set ? ` srcset="${p.set}" sizes="(max-width: 749px) 100vw, 50vw"` : ''} alt="${alt.replace(/"/g, '&quot;')}"${i ? ' loading="lazy"' : ''}></div>`).join('')
      + (photos.length > 1
        ? `<button type="button" class="valma-split__arrow" data-dir="-1" aria-label="Photo précédente">←</button><button type="button" class="valma-split__arrow valma-split__arrow--next" data-dir="1" aria-label="Photo suivante">→</button><div class="valma-split__dots">${photos.map((_, i) => `<button type="button" data-index="${i}" aria-label="Photo ${i + 1}"${i === 0 ? ' class="is-on"' : ''}></button>`).join('')}</div>`
        : '');
    root.dataset.index = '0';
    delete root.dataset.manual; // nouvelle pierre : le défilement auto reprend (sauf curseur sur la photo)
    playSlides(root);
  };
  document.querySelectorAll('[data-valma-slides]').forEach(initSlides);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-valma-slides]').forEach(initSlides));

  // Combo de la home : choix de la pierre → défilent d'abord les combos vus en photo avec cette pierre
  // (valma-combos-data : 3 bracelets [handle, pierre] + empreinte de la photo), puis les photos du combo par défaut.
  // Les 3 bracelets, le total et le bouton suivent la photo affichée.
  const comboProducts = new Map();
  const fetchProduct = (handle) => {
    if (!comboProducts.has(handle)) {
      const root = window.Shopify?.routes?.root || '/';
      comboProducts.set(handle, fetch(`${root}products/${handle}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null));
    }
    return comboProducts.get(handle);
  };
  const gammeOf = (handle) => (handle.startsWith('bracelet-big-jumbo') ? 'Big Jumbo' : handle.startsWith('bracelet-jumbo') ? 'Jumbo' : handle.startsWith('bracelet-medio') ? 'Medio' : '');
  const sizedUrl = (src, width) => {
    const url = new URL(src.startsWith('//') ? `https:${src}` : src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  };
  const comboSlides = async (section, stone) => {
    const picker = section.querySelector('[data-valma-combo-stones]');
    const fallback = JSON.parse(picker.querySelector('[data-valma-combo-data]').textContent)[stone];
    const all = JSON.parse(section.querySelector('[data-valma-combos]')?.textContent || '{"combos":[]}').combos;
    const size = section.dataset.size;
    const count = (c) => c.items.filter((i) => i[1] === stone).length;
    const photoCombos = all.filter((c) => count(c) > 0).sort((a, b) => count(b) - count(a));
    const handles = [...new Set(photoCombos.flatMap((c) => c.items.map((i) => i[0])))];
    const products = Object.fromEntries(await Promise.all(handles.map(async (h) => [h, await fetchProduct(h)])));
    const slides = [];
    for (const combo of photoCombos) {
      let photo = null;
      for (const [h] of combo.items) {
        const src = products[h]?.images?.find((u) => u.includes(`-${combo.d}`));
        if (src) { photo = src; break; }
      }
      if (!photo) continue;
      const items = combo.items.map(([h, s]) => {
        const p = products[h];
        const v = p && (p.variants.find((x) => x.option1 === s && x.option2 === size) || p.variants.find((x) => x.option1 === s));
        if (!v) return null;
        return { id: v.id, price: v.price, url: `${p.url}?variant=${v.id}`, img: sizedUrl(v.featured_image?.src || p.featured_image, 400), title: p.title, gamme: gammeOf(h), stone: s };
      });
      if (items.some((i) => !i)) continue;
      slides.push({ photo: { src: sizedUrl(photo, 1000), set: [600, 1000, 1600].map((w) => `${sizedUrl(photo, w)} ${w}w`).join(', ') }, items });
    }
    if (fallback) {
      const items = fallback.items.map((i) => ({ ...i, stone }));
      const photos = fallback.photos.length ? fallback.photos : [];
      photos.forEach((photo) => slides.push({ photo, items }));
      if (!slides.length) slides.push({ photo: null, items });
    }
    return slides;
  };
  const renderComboItems = (section, items) => {
    let total = 0;
    section.querySelectorAll('.valma-split__combo [data-variant]').forEach((card, i) => {
      const item = items[i];
      if (!item) return;
      total += item.price;
      card.dataset.variant = item.id;
      card.href = item.url;
      const img = card.querySelector('img');
      if (img && img.getAttribute('src') !== item.img) { img.removeAttribute('srcset'); img.src = item.img; img.alt = item.title; }
      card.querySelector('b').textContent = item.gamme || '';
      card.querySelector('small').innerHTML = `${item.title}${item.stone ? ` · ${item.stone}` : ''} · <span data-valma-combo-price>${formatMoney(item.price)}</span>`;
    });
    section.querySelector('[data-valma-combo-total]').textContent = formatMoney(total);
    section.querySelector('[data-valma-combo-discounted]').textContent = formatMoney(Math.round(total * 0.85));
  };
  const showComboStone = async (section, stone) => {
    const token = (section.dataset.comboToken = String(Date.now()));
    const slides = await comboSlides(section, stone);
    if (section.dataset.comboToken !== token || !slides.length) return;
    const root = section.querySelector('[data-valma-combo-slides]');
    section.valmaComboSlides = slides;
    renderComboItems(section, slides[0].items);
    if (root) {
      const photos = slides.map((s) => s.photo).filter(Boolean);
      if (photos.length === slides.length) setSlides(root, photos, stone);
    }
  };
  document.addEventListener('valma:slide', (event) => {
    const section = event.target.closest?.('[data-valma-combo]');
    const slide = section?.valmaComboSlides?.[event.detail.index];
    if (slide) renderComboItems(section, slide.items);
  });
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-valma-combo-stones] button[data-stone]');
    if (!button) return;
    const picker = button.closest('[data-valma-combo-stones]');
    picker.querySelectorAll('button[data-stone]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
    picker.querySelector('[data-valma-combo-stone]').textContent = button.dataset.stone;
    showComboStone(picker.closest('[data-valma-combo]'), button.dataset.stone);
  });
  // Au premier affichage : combos photo de la pierre par défaut (chargés quand la section approche de l'écran)
  const comboObserver = new IntersectionObserver((entries) => entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    comboObserver.unobserve(entry.target);
    const current = entry.target.querySelector('[data-valma-combo-stones] button[aria-pressed="true"]');
    if (current) showComboStone(entry.target, current.dataset.stone);
  }), { rootMargin: '400px' });
  document.querySelectorAll('[data-valma-combo]').forEach((section) => comboObserver.observe(section));

  // Tiroir panier : « Appliquer » le code de bienvenue → remplit le champ Réduction du panier et l'envoie.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-valma-apply-code]');
    if (!button) return;
    const scope = button.closest('cart-drawer-component, .cart-page, main') || document;
    const input = scope.querySelector('cart-discount-component input[name="discount"]') || document.querySelector('cart-discount-component input[name="discount"]');
    const form = input?.closest('form');
    if (!input || !form) { window.location.href = `/discount/${encodeURIComponent(button.dataset.valmaApplyCode)}?redirect=/cart`; return; }
    input.value = button.dataset.valmaApplyCode;
    button.disabled = true;
    button.textContent = '…';
    form.requestSubmit();
  });

  // Page catégorie (sections/valma-collection) ----------------------------------------------------------------
  // « + » d'une carte de la grille : ouvre / ferme le panneau taille + ajout (mobile ; au survol sur desktop).
  document.addEventListener('click', (event) => {
    const plus = event.target.closest('[data-valma-gcard-quick]');
    const open = document.querySelector('.valma-gcard.is-quick');
    if (plus) {
      const card = plus.closest('.valma-gcard');
      if (open && open !== card) open.classList.remove('is-quick');
      card.classList.toggle('is-quick');
      return;
    }
    if (open && !event.target.closest('.valma-gcard__quick')) open.classList.remove('is-quick');
  });
  // Menus Filtrer / Trier : un seul ouvert à la fois, fermés par un toucher sur le fond (feuille mobile).
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-valma-open-dd]');
    const dds = document.querySelectorAll('.valma-dd');
    if (opener) {
      dds.forEach((dd) => { dd.open = dd.dataset.valmaDd === opener.dataset.valmaOpenDd; });
      return;
    }
    const dd = event.target.closest('.valma-dd');
    if (dd && event.target === dd) { dd.open = false; return; }
    dds.forEach((other) => { if (other !== dd) other.open = false; });
  });
  // Phrase d'intro tronquée sur mobile : un toucher l'ouvre en entier.
  document.addEventListener('click', (event) => {
    const lead = event.target.closest('[data-valma-lead]');
    if (lead) lead.classList.toggle('is-open');
  });
  // « Voir plus de bijoux » : ajoute la page suivante à la grille sans recharger (lien normal si le JS ne charge pas).
  document.addEventListener('click', async (event) => {
    const more = event.target.closest('[data-valma-more]');
    if (!more || event.metaKey || event.ctrlKey) return;
    event.preventDefault();
    const grid = document.querySelector('[data-valma-grid]');
    if (!grid || more.getAttribute('aria-busy') === 'true') return;
    more.setAttribute('aria-busy', 'true');
    const label = more.textContent;
    more.textContent = 'Chargement…';
    try {
      const html = await fetch(more.href).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc.querySelectorAll('[data-valma-grid] > .valma-gcard').forEach((card) => grid.appendChild(document.adoptNode(card)));
      const seen = grid.querySelectorAll('.valma-gcard').length;
      const total = Number(more.dataset.total) || seen;
      document.querySelector('[data-valma-seen]').textContent = String(seen);
      document.querySelector('[data-valma-prog]').style.width = `${Math.min(100, Math.round((seen / total) * 100))}%`;
      const next = doc.querySelector('[data-valma-more]');
      if (next) { more.href = next.href; more.textContent = label; more.removeAttribute('aria-busy'); }
      else more.remove();
    } catch (error) {
      console.error(error);
      window.location.href = more.href;
    }
  });

  // Aperçu produit en fenêtre ([data-valma-quickview], ex. œil du module combo) : photos de la variante (même pierre),
  // nom, options, prix, description et lien vers la fiche. Le lien reste un vrai lien si le JS ne charge pas.
  const escapeHtml = (str) => String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  let quickview;
  const openQuickview = async (link) => {
    const url = new URL(link.href, window.location.origin);
    const product = await fetch(`${url.pathname}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    if (!product) { window.location.href = link.href; return; }
    const variantId = Number(url.searchParams.get('variant'));
    const variant = product.variants.find((v) => v.id === variantId) || product.variants[0];
    const values = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    const square = (m) => m.aspect_ratio >= 0.95 && m.aspect_ratio <= 1.05;
    let media = product.media.filter((m) => m.media_type === 'image' && values.includes(m.alt));
    if (!media.length) media = product.media.filter((m) => m.media_type === 'image').slice(0, 1);
    media.sort((a, b) => (a.alt === variant.option1 && square(a) ? -1 : 0) - (b.alt === variant.option1 && square(b) ? -1 : 0));
    if (!quickview) {
      quickview = document.createElement('dialog');
      quickview.className = 'valma-qv';
      quickview.addEventListener('click', (event) => {
        if (event.target === quickview || event.target.closest('.valma-qv__close')) { quickview.close(); return; }
        const navButton = event.target.closest('.valma-qv__nav');
        if (!navButton) return;
        const strip = quickview.querySelector('.valma-qv__media');
        const max = strip.scrollWidth - strip.clientWidth;
        let next = strip.scrollLeft + Number(navButton.dataset.dir) * strip.clientWidth;
        if (next > max + 2) next = 0;
        if (next < -2) next = max;
        strip.scrollTo({ left: next });
      });
      document.body.appendChild(quickview);
    }
    const imgs = media.slice(0, 6).map((m, i) => `<img src="${m.src}${m.src.includes('?') ? '&' : '?'}width=900" alt="${escapeHtml(m.alt || product.title)}" class="${square(m) ? 'is-packshot' : ''}"${i ? ' loading="lazy"' : ''}>`).join('');
    const options = product.options.map((o, i) => `${escapeHtml(o.name)} : ${escapeHtml(values[i] || '')}`).join(' · ');
    const nav = media.length > 1 ? '<button type="button" class="valma-qv__nav valma-qv__nav--prev" data-dir="-1" aria-label="Photo précédente">←</button><button type="button" class="valma-qv__nav valma-qv__nav--next" data-dir="1" aria-label="Photo suivante">→</button>' : '';
    quickview.innerHTML = `<button type="button" class="valma-qv__close" aria-label="Fermer">×</button>${nav}
      <div class="valma-qv__in">
        <div class="valma-qv__media">${imgs}</div>
        <div class="valma-qv__txt">
          <span class="valma-qv__meta">${escapeHtml(product.type)}</span>
          <h3>${escapeHtml(product.title)}</h3>
          <span class="valma-qv__meta">${options}</span>
          <span class="valma-qv__price">${formatMoney(variant.price)}</span>
          <div class="valma-qv__desc">${product.description || ''}</div>
          <a class="valma-qv__cta" href="${escapeHtml(link.href)}">Voir la fiche produit</a>
        </div>
      </div>`;
    quickview.showModal();
  };
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-valma-quickview]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    openQuickview(link);
  });

  // Fenêtre de bienvenue −10 % (section valma-welcome) : première visite, après quelques secondes, une fois tous les
  // N jours (fermée ou inscrite). Rouverte directement après l'envoi du formulaire pour afficher le code ou l'erreur.
  const welcome = document.querySelector('[data-valma-welcome]');
  if (welcome) {
    const KEY = 'valma_welcome_until';
    const snooze = () => { try { localStorage.setItem(KEY, String(Date.now() + Number(welcome.dataset.days || 30) * 864e5)); } catch (e) {} };
    const snoozed = () => { try { return Number(localStorage.getItem(KEY) || 0) > Date.now(); } catch (e) { return false; } };
    const open = () => { if (!welcome.open && !document.querySelector('dialog[open]')) welcome.showModal(); };
    welcome.addEventListener('close', snooze);
    // À l'envoi : fermer la fenêtre pour que la vérification anti-robot de Shopify (hCaptcha, affichée sous la couche
    // modale sinon) soit accessible ; la page revient ensuite avec la fenêtre ouverte sur le code (data-open-now).
    welcome.closest('form')?.addEventListener('submit', () => { if (welcome.open) welcome.close(); });
    welcome.addEventListener('click', (event) => {
      if (event.target === welcome || event.target.closest('[data-valma-welcome-close]')) { welcome.close(); return; }
      const copy = event.target.closest('[data-valma-welcome-copy]');
      if (copy) {
        navigator.clipboard?.writeText(copy.dataset.code).then(() => {
          copy.querySelector('span').textContent = '✓ Copié';
          setTimeout(() => { copy.querySelector('span').textContent = 'Copier'; }, 1800);
        }).catch(() => {});
      }
    });
    if (welcome.dataset.openNow) {
      // Shopify revient sur /?customer_posted=true#ValmaWelcomeForm (formulaire en bas de page) : on reste en haut
      if (window.location.hash === '#ValmaWelcomeForm') { history.replaceState(null, '', window.location.pathname + window.location.search); window.scrollTo(0, 0); }
      snooze(); open();
    }
    else if (!snoozed() && !window.Shopify?.designMode) setTimeout(open, Number(welcome.dataset.delay || 4) * 1000);
    document.addEventListener('shopify:section:select', (event) => { if (event.target.contains(welcome)) welcome.showModal(); });
  }

  // Bannière cookies Shopify : textes en français (la boutique peut être en anglais ; sinon on ne touche à rien)
  const frenchConsent = () => {
    const banner = document.getElementById('shopify-pc__banner');
    if (!banner || banner.dataset.valmaFr) return;
    const set = (sel, txt) => { const el = banner.querySelector(sel); if (el) el.textContent = txt; };
    if (!/cookie consent|cookies/i.test(banner.querySelector('#shopify-pc__banner__body-title')?.textContent || '')) return;
    banner.dataset.valmaFr = '1';
    set('#shopify-pc__banner__body-title', 'Un cookie ?');
    const p = banner.querySelector('.shopify-pc__banner__body p');
    const link = banner.querySelector('#shopify-pc__banner__body-policy-link');
    if (p) {
      p.textContent = 'Valma et ses partenaires, dont Shopify, utilisent des cookies pour personnaliser ta visite, mesurer l’audience et te montrer des publicités, uniquement si tu les acceptes. ';
      if (link) { link.textContent = 'Politique de confidentialité'; p.appendChild(link); }
    }
    set('#shopify-pc__banner__btn-accept', 'Accepter');
    set('#shopify-pc__banner__btn-decline', 'Refuser');
    set('#shopify-pc__banner__btn-manage-prefs', 'Choisir mes préférences');
  };
  new MutationObserver(frenchConsent).observe(document.body, { childList: true });
  frenchConsent();

  // Tuiles « Tout l'atelier » avec plusieurs photos : elles défilent au survol (1,6 s), retour à la 1re ensuite.
  document.querySelectorAll('.valma-univ__tile').forEach((tile) => {
    const imgs = [...tile.querySelectorAll('img')];
    if (imgs.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let timer, i = 0;
    const show = (n) => { i = n % imgs.length; tile.dataset.photo = String(i); imgs.forEach((im, k) => im.classList.toggle('is-on', k === i)); };
    tile.addEventListener('mouseenter', () => { clearInterval(timer); imgs.forEach((im) => { im.loading = 'eager'; }); show(1); timer = setInterval(() => show(i + 1), 1600); });
    tile.addEventListener('mouseleave', () => { clearInterval(timer); show(0); delete tile.dataset.photo; });
  });

  // Mobile / tactile (pas de survol) --------------------------------------------------------------------------
  const noHover = window.matchMedia('(hover: none)');
  // Cartes produit : glisser horizontalement dans la carte (hors carrousels horizontaux, qui défilent) ou toucher
  // les points → bascule packshot / photo portée. Un toucher ailleurs ouvre la fiche comme avant.
  const SWIPE_CARD = '.valma-rcard, .valma-pc, .resource-card, .valma-gm, .valma-gcard';
  const toggleAlt = (card, force) => {
    const on = force ?? !card.classList.contains('is-alt');
    card.classList.toggle('is-alt', on);
    card.querySelectorAll('.valma-swipe-dots i').forEach((dot, n) => dot.classList.toggle('is-on', n === (on ? 1 : 0)));
  };
  const inHorizontalScroller = (el) => {
    for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) {
      const ox = getComputedStyle(n).overflowX;
      if ((ox === 'auto' || ox === 'scroll') && n.scrollWidth > n.clientWidth + 4) return true;
    }
    return false;
  };
  let touch = null;
  document.addEventListener('touchstart', (event) => {
    const card = event.target.closest?.(SWIPE_CARD);
    if (!noHover.matches || !card || !card.querySelector('.valma-swipe-dots')) { touch = null; return; }
    touch = { card, x: event.touches[0].clientX, y: event.touches[0].clientY };
  }, { passive: true });
  document.addEventListener('touchend', (event) => {
    if (!touch) return;
    const { card, x, y } = touch; touch = null;
    const dx = event.changedTouches[0].clientX - x; const dy = event.changedTouches[0].clientY - y;
    if (Math.abs(dx) < 35 || Math.abs(dx) < Math.abs(dy) * 1.5 || inHorizontalScroller(card)) return;
    toggleAlt(card, dx < 0);
    card.dataset.swipedAt = String(Date.now());
  }, { passive: true });
  document.addEventListener('click', (event) => {
    const card = event.target.closest?.(SWIPE_CARD);
    if (!card) return;
    if (event.target.closest('.valma-swipe-dots')) { event.preventDefault(); event.stopPropagation(); toggleAlt(card); return; }
    if (Date.now() - Number(card.dataset.swipedAt || 0) < 500) { event.preventDefault(); event.stopPropagation(); }
  }, true);

  // Tuiles « Tout l'atelier » et « Portés par vous » : les photos s'enchaînent seules (2,5 s) quand la tuile est visible.
  if (noHover.matches && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const cycle = (tile) => {
      const univ = [...tile.querySelectorAll('img')];
      if (tile.classList.contains('valma-univ__tile')) {
        if (univ.length < 2) return null;
        let i = 0;
        return () => { i = (i + 1) % univ.length; tile.dataset.photo = String(i); univ.forEach((im, k) => { im.loading = 'eager'; im.classList.toggle('is-on', k === i); }); };
      }
      if (!tile.querySelector('.valma-ugc__b')) return null;
      return () => tile.classList.toggle('is-alt');
    };
    const timers = new WeakMap();
    const io = new IntersectionObserver((entries) => entries.forEach((entry) => {
      const tile = entry.target;
      clearInterval(timers.get(tile));
      if (entry.isIntersecting) { const step = cycle(tile); if (step) timers.set(tile, setInterval(step, 2500)); }
    }), { threshold: 0.6 });
    document.querySelectorAll('.valma-univ__tile, .valma-ugc > a').forEach((tile) => io.observe(tile));
  }

  // Rafraîchissement des blocs Valma au changement de variante.
  document.addEventListener(
    'shopify:product:select',
    (event) => {
      if (!event.promise) return;
      event.promise
        .then(({ detail }) => {
          const html = detail?.html;
          if (!html) return;
          const doc = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html;
          document.querySelectorAll('[data-valma-refresh][id]').forEach((el) => {
            const fresh = doc.getElementById(el.id);
            if (fresh) el.replaceWith(fresh.cloneNode(true));
          });
        })
        .catch(() => {});
    },
    true
  );
})();
